import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {ConfirmDialog} from 'primereact/confirmdialog'
import {PickList} from 'primereact/picklist'
import {ProgressSpinner} from 'primereact/progressspinner'
import {useCallback, useEffect, useRef} from 'react'
import {HTTP_STATUS_OK} from '../../Constants'
import {ReactComponent as DuoSave} from '../../assets/icons/DuoSave.svg'
import {ReactComponent as DuoWifi} from '../../assets/icons/DuoWifi.svg'
import {ReactComponent as DuoWifiScan} from '../../assets/icons/DuoWifiScan.svg'
import {ReactComponent as FoundWifiIcon} from '../../assets/icons/FoundWifiIcon.svg'
import {ReactComponent as WifiAuthenticated} from '../../assets/icons/WifiAuthenticated.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {detailEvent} from '../../utils/DetailPageReloadUtils'
import {useAuth} from '../authentication/AuthProvider'
import {ECGDeviceTypes} from '../ecg/ECGConstants'
import StyledTitle from '../layout/StyledTitle'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import {
    addNetwork,
    getNetworkSuggestions,
    removeNetwork,
    scanNetwork,
} from './WatchConfigEndPoint'
import {WatchNetworkForm} from './WatchNetworkForm'

const WatchNetwork = ({id = null}) => {
    useSignals()

    //#region Context
    const {currentViewedUser} = usePatientContext()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region Ref
    const watchNetworkForm = useRef(null)
    //#endregion

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const source = useSignal([])
    const target = useSignal([])
    const selectedNetwork = useSignal({})
    const isScanning = useSignal(false)
    //#endregion

    //#region Handle Function
    const handleScanNetwork = async () => {
        const scanStatus = await scanNetwork(id)
        if (scanStatus === HTTP_STATUS_OK) {
            source.value = []
            isScanning.value = true
            showNotification(
                severityNotification.success,
                'Scan network signal sent successfully',
            )
        }
    }

    const hideMiddleCharacters = str => {
        if (!str || str?.length <= 2) {
            return str
        }

        const firstChar = str[0]
        const lastChar = str[str.length - 1]
        const middleLength = str.length - 2
        const hiddenMiddle = '*'.repeat(middleLength)

        return `${firstChar}${hiddenMiddle}${lastChar}`
    }

    const handleGetSuggestionList = async () => {
        if (!!id) {
            const suggestionList = await getNetworkSuggestions(id)
            if (suggestionList) {
                const targetData = suggestionList.map(item => ({
                    id: item?.id || null,
                    ssid: item?.ssid || '',
                    passphrase: item?.passphrase || '',
                }))
                target.value = targetData
            }
        }
    }

    const onRemoveWatchNetwork = e => {}

    const onAddWatchNetwork = e => {
        if (e.value?.length === 0) return

        watchNetworkForm.current.showForm({
            ssid: e.value[0].ssid ? e.value[0].ssid.replace(/['"]+/g, '') : '',
            passphrase: e.value[0].passphrase,
        })
    }

    const handleAddWatchNetwork = useCallback(
        async value => {
            if (!value?.ssid || !value?.passphrase) {
                showNotification(
                    severityNotification.error,
                    'Please fill in the required fields',
                )
                return
            }

            let toPostData = {
                id: value?.id,
                ssid: value?.ssid,
                passphrase: value?.passphrase,
                userId: id ? id : null,
            }

            const networkResponse = await addNetwork(toPostData)
            if (!!networkResponse?.id) {
                showNotification(
                    severityNotification.success,
                    'Network saved successfully',
                )
                const sourceData = source.value.filter(item => {
                    if (item?.ssid) {
                        return item.ssid.replace(/['"]+/g, '') !== value.ssid
                    }
                })

                source.value = sourceData

                const targetData = [...target.value]

                if (value?.id) {
                    const index = targetData.findIndex(
                        item => item.id === value.id,
                    )
                    if (index !== -1) {
                        targetData.splice(index, 1)
                    }
                }

                targetData.push({
                    ...networkResponse,
                })

                target.value = [...targetData]
            } else {
                showNotification(
                    'error',
                    'Failed to save network. Please try again.',
                )
            }

            watchNetworkForm.current.hideForm()
        },
        [source, target, id],
    )

    const handleRemoveWatchNetwork = async () => {
        if (!!selectedNetwork.value?.id) {
            const removeStatus = await removeNetwork(selectedNetwork.value?.id)

            if (removeStatus === HTTP_STATUS_OK) {
                showNotification(
                    severityNotification.success,
                    'Network removed successfully',
                )
                const targetData = target.value.filter(
                    item => item.id !== selectedNetwork.value.id,
                )
                target.value = targetData
            } else {
                showNotification(
                    severityNotification.error,
                    'Failed to remove network. Please try again.',
                )
            }
        } else {
            showNotification(
                severityNotification.error,
                'Failed to remove network. Please try again.',
            )
        }
    }

    //#endregion

    //#region Template
    const targetItemTemplate = item => {
        return (
            <div
                key={item?.ssid}
                className="flex align-items-center justify-content-between">
                <div className="flex flex-wrap p-2 align-items-center gap-3">
                    <WifiAuthenticated height={30} width={30} />
                    <div className="flex-1 flex flex-column gap-2">
                        <span className="font-bold">
                            {' '}
                            {item && !!item?.ssid
                                ? item.ssid.replace(/['"]+/g, '')
                                : ''}
                        </span>
                        <span>
                            <small>
                                {item?.passphrase
                                    ? hideMiddleCharacters(item?.passphrase)
                                    : translateCommons('Not configured yet')}
                            </small>
                        </span>
                    </div>
                </div>
                <div>
                    <Button
                        style={{
                            zIndex: 2,
                        }}
                        className="p-button-rounded  p-button-info mr-2"
                        severity="info"
                        icon="pi pi-pencil"
                        tooltip={translateCommons('Edit network')}
                        tooltipOptions={{position: 'left'}}
                        onClick={() => watchNetworkForm.current?.showForm(item)}
                    />
                    <Button
                        style={{
                            zIndex: 2,
                        }}
                        icon="pi pi-trash"
                        severity="danger"
                        className="p-button-rounded p-button-danger p-button-outlined "
                        tooltip={translateCommons('Remove network')}
                        tooltipOptions={{position: 'left'}}
                        onClick={() => {
                            selectedNetwork.value = item
                            visible.value = true
                        }}
                    />
                </div>
            </div>
        )
    }

    const sourceItemTemplate = item => {
        return (
            <div
                key={item?.ssid}
                className="flex flex-wrap p-2 align-items-center gap-3">
                <FoundWifiIcon height={30} width={30} />
                <div className="flex-1 flex flex-column gap-2">
                    <span className="font-bold">
                        {item && !!item?.ssid
                            ? item.ssid.replace(/['"]+/g, '')
                            : ''}
                    </span>
                    <small>
                        {item?.passphrase
                            ? item.passphrase
                            : translateCommons('Not configured yet')}
                    </small>
                </div>
            </div>
        )
    }

    //#endregion

    //#region Effect
    useEffect(() => {
        handleGetSuggestionList()
    }, [id])

    useEffect(() => {
        if (!id) {
            return
        }

        detailEvent.on('WIFI-CONNECTION-RELOAD-' + id, data => {
            if (data) {
                const sourceData = data.map(item => ({
                    ssid: item,
                    passphrase: '',
                }))
                source.value = sourceData
                isScanning.value = false
            }
        })

        return () => {
            detailEvent.removeListener('WIFI-CONNECTION-RELOAD-' + id)
        }
    }, [id])

    useEffect(() => {
        if (isScanning.value) {
            const timer = setTimeout(() => {
                isScanning.value = false

                showNotification(
                    severityNotification.info,
                    'Make sure the watch is connected to the internet',
                )
            }, 10000)

            return () => clearTimeout(timer)
        }
    }, [isScanning])
    //#endregion

    return (
        <div className="mb-3">
            <div className="flex mb-2 border-round py-3 px-2 flex-wrap align-items-center justify-content-between w-full">
                {/* <h5
                    className={`pl-2 text-black font-bold w-full m-0 py-2 text-center  md:text-left`}>
                    <span className="mr-3">
                        <DuoWifi style={{width: '20px', height: '20px'}} />
                    </span>
                    {translateCommons('Network Configuration')}
                </h5> */}
                <StyledTitle 
                    title={translateCommons('Network Configuration')} 
                    icon={<DuoWifi/>} 
                    description= {translateCommons(
                                `This configuration section is used for pre-configuring the network for the patient's device such as Wifi, Hostspot`,
                    )}
                />
                 <div>
                    <Button
                        label={translateCommons('Add a network')}
                        icon="pi pi-plus "
                        onClick={() => watchNetworkForm.current?.showForm({})}
                        className="bg-gray-700 hover:bg-gray-900 border-0"
                    />
                </div>
            </div>
            <PickList
                dataKey="ssid"
                source={source.value}
                target={target.value}
                onChange={() => {}}
                filterBy="name"
                breakpoint="1280px"
                sourceHeader={
                    <div className="flex align-items-center justify-content-between">
                        <div>
                            <h5 className="font-bold mb-0">
                                {translateCommons('Available Network')}
                            </h5>
                            <small className="font-normal">
                                {translateCommons(
                                    'List of available network nearby the watch',
                                )}
                            </small>
                        </div>
                        {currentViewedUser?.deviceBrand ===
                            ECGDeviceTypes.samsung && (
                            <div>
                                {isScanning.value && (
                                    <div
                                        className="pointer"
                                        onClick={() =>
                                            (isScanning.value = false)
                                        }>
                                        <ProgressSpinner
                                            style={{
                                                width: '30px',
                                                height: '30px',
                                            }}
                                        />
                                    </div>
                                )}

                                {!isScanning.value && (
                                    <div
                                        className="flex align-items-center pointer"
                                        onClick={() => handleScanNetwork()}>
                                        <div style={{color: '#0f766e'}}>
                                            {translateCommons('Click to scan')}
                                        </div>
                                        <div>
                                            <span className="mr-2 align-self-center"></span>
                                            <DuoWifiScan
                                                height={30}
                                                width={30}
                                                color='#0f766e'
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                }
                targetHeader={
                    <div className="flex align-items-center justify-content-between">
                        <div>
                            <h5 className="font-bold mb-0">
                                {translateCommons('Saved Network')}
                            </h5>
                            <small className="font-normal">
                                {translateCommons(
                                    'A saved list of suggestion network for the watch',
                                )}
                            </small>
                        </div>
                        <div>
                            <span className="pointer">
                                <DuoSave height={30} width={30} color='#0f766e'/>
                            </span>
                        </div>
                    </div>
                }
                sourceStyle={{height: '24rem'}}
                targetStyle={{height: '24rem'}}
                sourceFilterPlaceholder={translateCommons('Search by name')}
                targetFilterPlaceholder={translateCommons('Search by name')}
                showSourceControls={false}
                showTargetControls={false}
                sourceItemTemplate={sourceItemTemplate}
                targetItemTemplate={targetItemTemplate}
                onMoveToSource={e => onRemoveWatchNetwork(e)}
                onMoveToTarget={e => onAddWatchNetwork(e)}
            />
            <ConfirmDialog
                visible={visible.value}
                onHide={() => (visible.value = false)}
                message={
                    <h6 className="m-0">
                        <b>
                            {translateCommons(
                                'Are you sure you want to remove this network?',
                            )}
                        </b>
                    </h6>
                }
                header={translateCommons('Confirmation')}
                icon="pi pi-exclamation-triangle"
                accept={() => handleRemoveWatchNetwork()}
                reject={() => {
                    visible.value = false
                    selectedNetwork.value = {}
                }}
            />
            <WatchNetworkForm
                ref={watchNetworkForm}
                onAddNetwork={handleAddWatchNetwork}
            />
        </div>
    )
}

export default WatchNetwork
